import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChapterPinHandlers } from '../assistant/ChapterTemplates';
import {
  chartVisual,
  kpiVisual,
  tableVisual,
  visualPinId,
  visualTileFrom,
  widgetPinId,
  widgetTileFrom,
} from '../assistant/dashboardRecipes';
import type { DashboardTile } from '../assistant/dashboardTypes';
import { useDashboardStore } from '../assistant/useDashboardStore';
import { widgetParamKey } from '../assistant/widgets/widgetTypes';
import { cardRecord, type CardMessage } from './cards';

/** The Board (pinned outputs per scope) plus pin buttons for any card. */
export function useBoard(scopeKey: string) {
  const { t } = useTranslation('assistant');
  const board = useDashboardStore(scopeKey);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(id);
  }, [toast]);

  const { pinTile, unpinTile, isPinned, tiles } = board;
  const keep = useCallback(
    async (tile: DashboardTile, label: string) => {
      await pinTile(tile);
      setToast(t('view.kept', { label }));
    },
    [pinTile, t],
  );

  const pinsFor = useCallback(
    (message: CardMessage, index: number): ChapterPinHandlers => {
      const record = cardRecord(message, index);
      return {
        isPinned,
        pinIdForChart: (chart, i) => visualPinId(record.id, 'chart', chart.dataRef || String(i)),
        pinIdForTable: (table, i) => visualPinId(record.id, 'table', table.dataRef || String(i)),
        pinIdForKpi: kpi => visualPinId(record.id, 'kpi', kpi.field || kpi.label),
        pinIdForWidget: widget => widgetPinId(record.id, widgetParamKey(widget)),
        onPinChart: (chart, i) =>
          void keep(visualTileFrom(record, chartVisual(record, chart, i), tiles), chart.title || 'chart'),
        onPinTable: (table, i) =>
          void keep(visualTileFrom(record, tableVisual(record, table, i), tiles), table.title || 'table'),
        onPinKpi: kpi => void keep(visualTileFrom(record, kpiVisual(record, kpi), tiles), kpi.label),
        onPinWidget: widget => void keep(widgetTileFrom(record, widget, tiles), widget.title),
        onUnpin: id => void unpinTile(id),
      };
    },
    [isPinned, keep, tiles, unpinTile],
  );

  return { ...board, toast, setToast, pinsFor };
}

export type Board = ReturnType<typeof useBoard>;
